// Adapted from pinpointedu/frontend/apps/product/src/next/app/errors/ProductErrorBoundary.tsx
// (§9 of the frontend proposal), stripped of Sentry/analytics/deploy-chunk
// recovery -- those land later, if ever, behind their own PR. The boundary
// sits OUTSIDE Auth0ProviderWithNavigate in App.tsx on purpose: a lazy-import
// throw inside it would swallow handleRedirectCallback and strand the user on
// the callback path.
import { Component, type ErrorInfo, type ReactNode } from 'react';

type ErrorBoundaryState = {
  hasError: boolean;
  incidentId: string | null;
};

export class ErrorBoundary extends Component<{ children?: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children?: ReactNode }) {
    super(props);
    this.state = { hasError: false, incidentId: null };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true, incidentId: crypto.randomUUID() };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) console.error(error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className="flex min-h-screen items-center justify-center bg-background p-4"
          data-testid="error-boundary-fallback"
        >
          <div className="w-full max-w-lg rounded-lg border border-destructive/30 bg-card p-6 shadow-lg">
            <h1 className="mb-4 text-2xl font-bold text-destructive">Something went wrong</h1>
            <p className="mb-4 text-muted-foreground">
              MoneySkyline encountered a critical error and could not render.
            </p>
            <p className="mb-4 text-sm text-muted-foreground">
              Incident <span data-testid="incident-id">{this.state.incidentId}</span>
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded bg-primary px-4 py-2 text-primary-foreground"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
